import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Home,
  FileText,
  Bell,
  Search,
  User,
  LogOut,
  LogIn,
  Menu,
  X,
  Clock,
  Target,
  Settings,
  Calendar,
  CheckSquare,
  Trash2,
  CalendarCheck,
  CalendarDays,
  CreditCard,
  Lock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import SubscriptionLockOverlay from '../SubscriptionLockOverlay';
import styles from './EmployeeLayout.module.css';
import { apiServiceWithRefresh } from '../../utils/api';
import SupportWidget from '../common/SupportWidget/SupportWidget';

const EmployeeLayout = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationSearch, setNotificationSearch] = useState('');
  const [notificationTypeFilter, setNotificationTypeFilter] = useState('all');
  const [notificationReadFilter, setNotificationReadFilter] = useState('all');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentPages, setRecentPages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { logout, user, hasFullAccess, isInGracePeriod } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const profileDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const locallyReadIdsRef = useRef(new Set());

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleViewProfile = () => {
    navigate('/employee/profile');
    setIsProfileDropdownOpen(false);
  };

  const handleNavigation = (item) => {
    if (item.path) {
      navigate(item.path);
      setIsMobileMenuOpen(false);
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return `${Math.floor(diffInSeconds / 604800)} weeks ago`;
  };

  const handleDismissNotification = async (notificationId) => {
    try {
      const result = await apiServiceWithRefresh.dismissNotification(notificationId);
      if (result.success) {
        removeDismissedId(notificationId);
        setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
      }
    } catch (error) {
      // Silently handle dismiss failure
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getDismissedIds = () => {
    try {
      return new Set(JSON.parse(localStorage.getItem('dismissedNotifications') || '[]'));
    } catch { return new Set(); }
  };

  const saveDismissedId = (id) => {
    const ids = getDismissedIds();
    ids.add(id);
    const idsArray = [...ids];
    const capped = idsArray.length > 500 ? idsArray.slice(idsArray.length - 500) : idsArray;
    localStorage.setItem('dismissedNotifications', JSON.stringify(capped));
  };

  const removeDismissedId = (id) => {
    const ids = getDismissedIds();
    ids.delete(id);
    localStorage.setItem('dismissedNotifications', JSON.stringify([...ids]));
  };

  const handleDismissFromDropdown = (notificationId) => {
    saveDismissedId(notificationId);
    setNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, dismissed: true } : n)
    );
  };

  const filteredModalNotifications = notifications.filter(n => {
    if (notificationSearch) {
      const query = notificationSearch.toLowerCase();
      if (!n.title.toLowerCase().includes(query) && !n.message.toLowerCase().includes(query)) return false;
    }
    if (notificationTypeFilter !== 'all' && n.type !== notificationTypeFilter) return false;
    if (notificationReadFilter === 'unread' && n.read) return false;
    if (notificationReadFilter === 'read' && !n.read) return false;
    return true;
  });

  const handleShowAllMessages = () => {
    setIsNotificationDropdownOpen(false);
    setNotificationSearch('');
    setNotificationTypeFilter('all');
    setNotificationReadFilter('all');
    setIsNotificationModalOpen(true);
  };

  // Fetch notifications via useQuery with polling
  const { data: notificationsData } = useQuery({
    queryKey: ['notifications', 'employee'],
    queryFn: async () => {
      const result = await apiServiceWithRefresh.getNotifications();
      if (result.success) {
        return result.data || [];
      }
      return [];
    },
    refetchInterval: 300000,
  });

  // Process notifications data
  useEffect(() => {
    if (notificationsData) {
      const dismissedIds = getDismissedIds();
      const formattedNotifications = notificationsData.map(notif => {
        const isLocallyRead = locallyReadIdsRef.current.has(notif.id);
        if (notif.isRead && isLocallyRead) {
          locallyReadIdsRef.current.delete(notif.id);
        }
        return {
          id: notif.id,
          title: notif.title,
          message: notif.message,
          time: formatRelativeTime(notif.createdAt),
          type: notif.type,
          read: notif.isRead || isLocallyRead,
          dismissed: dismissedIds.has(notif.id)
        };
      });
      setNotifications(formattedNotifications);
    }
  }, [notificationsData]);

  // Auto-mark notifications as read when modal opens
  useEffect(() => {
    if (isNotificationModalOpen) {
      const unreadNotifications = notifications.filter(n => !n.read);
      if (unreadNotifications.length > 0) {
        unreadNotifications.forEach(n => locallyReadIdsRef.current.add(n.id));
        setNotifications(prev =>
          prev.map(n => n.read ? n : { ...n, read: true })
        );
        unreadNotifications.forEach(n => {
          apiServiceWithRefresh.markNotificationAsRead(n.id).catch(() => {});
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotificationModalOpen]);

  // Listen for custom event to open notification modal from dashboard
  useEffect(() => {
    const handleOpenNotificationModal = () => {
      setIsNotificationModalOpen(true);
    };

    window.addEventListener('openNotificationModal', handleOpenNotificationModal);
    return () => {
      window.removeEventListener('openNotificationModal', handleOpenNotificationModal);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setIsNotificationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load recent pages from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentPages');
    if (saved) {
      try {
        setRecentPages(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load recent pages');
      }
    }
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch clock status for dynamic sidebar (Clock In vs Clock Out)
  const { data: clockStatusData } = useQuery({
    queryKey: ['clock', 'status'],
    queryFn: async () => {
      const result = await apiServiceWithRefresh.getClockStatus();
      return result.success && result.data ? result.data : null;
    },
    staleTime: 30000,
  });
  const isClockedIn = clockStatusData?.isActive === true;

  // Menu items — sidebar item order depends on whether the user is clocked in
  const menuItems = useMemo(() => {
    const dashboard = { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/dashboard' };
    const bookings = user?.accessBookingManagement
      ? { id: 'booking-management', label: 'My Bookings', icon: CalendarDays, path: '/employee/bookings' }
      : { id: 'schedule', label: 'My Bookings', icon: Calendar, path: '/employee/schedule' };
    const targets = { id: 'goal-tracking', label: 'My Targets', icon: Target, path: '/employee/goal-tracking' };
    const pos = user?.accessPOS
      ? [{ id: 'pos', label: 'Point of Sale', icon: CreditCard, path: '/employee/pos' }]
      : [];
    const clockIn = { id: 'clock-in', label: 'Clock In', icon: LogIn, path: '/employee/clock-in' };
    const clockOut = { id: 'clock-out', label: 'Clock Out', icon: LogOut, path: '/employee/clock-out' };

    // [DISABLED] Checklists — hidden per business decision, re-enable when ready to launch as new features
    // const checklists = { id: 'checklists', label: 'Checklists', icon: CheckSquare, path: '/employee/checklists' };
    // [DISABLED] Leave — hidden per business decision, re-enable when ready to launch as new features
    // const leave = { id: 'leave', label: 'My Leave', icon: CalendarCheck, path: '/employee/leave' };
    // [DISABLED] HR Policies & MVV — hidden per business decision, re-enable when ready to launch as new features
    // const policies = { id: 'policies', label: 'HR Policies', icon: FileText, path: '/employee/policies' };
    // const companyValues = { id: 'company-values', label: 'Mission & Values', icon: Settings, path: '/employee/company-values' };

    if (isClockedIn) {
      return [dashboard, bookings, ...pos, targets, clockOut];
    }
    return [dashboard, clockIn, bookings, ...pos, targets];
  }, [user?.accessBookingManagement, user?.accessPOS, isClockedIn]);

  // Filter menu items based on search query
  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) {
      return recentPages.map(recentItem => {
        const menuItem = menuItems.find(item => item.id === recentItem.id);
        return menuItem || recentItem;
      }).slice(0, 5);
    }

    const query = searchQuery.toLowerCase();
    return menuItems.filter(item =>
      item.label.toLowerCase().includes(query) ||
      item.id.toLowerCase().includes(query) ||
      (item.path && item.path.toLowerCase().includes(query))
    ).slice(0, 8);
  }, [searchQuery, menuItems, recentPages]);

  // Handle navigation from search palette
  const handleSearchNavigate = useCallback((item) => {
    if (item.path) {
      navigate(item.path);

      const recentItem = { id: item.id, label: item.label, path: item.path };
      const newRecent = [recentItem, ...recentPages.filter(p => p.id !== item.id)].slice(0, 5);
      setRecentPages(newRecent);
      localStorage.setItem('recentPages', JSON.stringify(newRecent));

      setIsSearchOpen(false);
      setSearchQuery('');
      setSelectedIndex(0);
      setIsMobileMenuOpen(false);
    }
  }, [navigate, recentPages]);

  // Keyboard shortcuts for search palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
        setSearchQuery('');
        setSelectedIndex(0);
      }

      if (e.key === 'Escape' && isSearchOpen) {
        setIsSearchOpen(false);
        setSearchQuery('');
        setSelectedIndex(0);
      }

      if (isSearchOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => {
            const maxIndex = filteredResults.length - 1;
            return prev < maxIndex ? prev + 1 : prev;
          });
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (filteredResults[selectedIndex]) {
            handleSearchNavigate(filteredResults[selectedIndex]);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, selectedIndex, filteredResults, handleSearchNavigate]);

  // Render sidebar nav content (shared between desktop and mobile)
  const renderSidebarNav = () => (
    <>
      {menuItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        const isLocked = !hasFullAccess;
        return (
          <button
            key={item.id}
            className={`${styles.sidebarItem} ${isActive ? styles.sidebarItemActive : ''} ${isLocked ? styles.sidebarItemLocked : ''}`}
            onClick={() => isLocked ? null : handleNavigation(item)}
          >
            <Icon size={18} />
            <span>{item.label}</span>
            {isLocked && <Lock size={12} style={{ marginLeft: 'auto', color: '#94a3b8' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className={styles.appLayout}>
      {/* Topbar */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          {/* Mobile hamburger */}
          <button
            className={styles.mobileMenuBtn}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Logo */}
          <div
            className={styles.logoContainer}
            onClick={() => navigate('/dashboard')}
          >
            <img src="/LogoXtraBold.svg" alt="1nespa" className={styles.logoImage} />
            <div className={styles.logoTextContainer}>
              <div className={styles.logoBrandName}>1ne s.p.a.</div>
              <div className={styles.logoSubtitle}>Beauty & Business Coach</div>
            </div>
          </div>
        </div>

        <div className={styles.topbarRight}>
          {/* Search Trigger */}
          <button
            className={styles.searchTrigger}
            onClick={() => setIsSearchOpen(true)}
          >
            <Search size={16} className={styles.searchIcon} />
            <span className={styles.searchPlaceholder}>Search...</span>
          </button>

          {/* Notifications */}
          <div className={styles.notificationSection} ref={notificationDropdownRef}>
            <button
              className={styles.notificationBtn}
              onClick={() => {
                const opening = !isNotificationDropdownOpen;
                setIsNotificationDropdownOpen(opening);
                if (opening) {
                  const unreadNotifications = notifications.filter(n => !n.read);
                  if (unreadNotifications.length > 0) {
                    unreadNotifications.forEach(n => locallyReadIdsRef.current.add(n.id));
                    setNotifications(prev =>
                      prev.map(n => n.read ? n : { ...n, read: true })
                    );
                    unreadNotifications.forEach(n => {
                      apiServiceWithRefresh.markNotificationAsRead(n.id).catch(() => {});
                    });
                  }
                }
              }}
            >
              <Bell size={24} />
              {unreadCount > 0 && (
                <span className={styles.notificationBadge}>{unreadCount}</span>
              )}
            </button>

            {isNotificationDropdownOpen && (
              <div className={styles.notificationDropdown}>
                <div className={styles.notificationHeader}>
                  <h3>Notifications</h3>
                  {unreadCount > 0 && (
                    <span className={styles.notificationCount}>{unreadCount} new</span>
                  )}
                </div>

                <div className={styles.notificationsList}>
                  {notifications.filter(n => !n.dismissed).length === 0 ? (
                    <div className={styles.emptyNotifications}>
                      <Bell size={48} className={styles.emptyIcon} />
                      <p className={styles.emptyText}>No new notifications</p>
                      <p className={styles.emptySubtext}>You're all caught up!</p>
                    </div>
                  ) : (
                    notifications.filter(n => !n.dismissed).map(notification => (
                      <div
                        key={notification.id}
                        className={`${styles.notificationItem} ${notification.read ? styles.read : styles.unread}`}
                      >
                        <div className={styles.notificationContent}>
                          <h4 className={styles.notificationTitle}>{notification.title}</h4>
                          <p className={styles.notificationMessage}>{notification.message}</p>
                          <span className={styles.notificationTime}>{notification.time}</span>
                        </div>
                        <div className={styles.notificationActions}>
                          <button
                            className={styles.dismissBtn}
                            onClick={() => handleDismissFromDropdown(notification.id)}
                            title="Dismiss"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className={styles.notificationFooter}>
                  <button className={styles.showAllBtn} onClick={handleShowAllMessages}>
                    Show all messages
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Profile */}
          <div className={styles.profileSection} ref={profileDropdownRef}>
            <button
              className={styles.profileBtn}
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
            >
              <div className={styles.profileAvatar}>
                <User size={16} />
              </div>
              <div className={styles.profileInfo}>
                <span className={styles.profileName}>
                  {user?.preferredName || (user?.firstName ? `${user.firstName} ${user.lastName}` : 'Employee')}
                </span>
                <span className={styles.profileRole}>Staff Member</span>
              </div>
            </button>

            {isProfileDropdownOpen && (
              <div className={styles.profileDropdown}>
                <div className={styles.profileDropdownItem} onClick={handleViewProfile}>
                  <User size={16} />
                  <span>View Profile</span>
                </div>
                <div className={styles.dropdownDivider}></div>
                <div className={styles.profileDropdownItem} onClick={handleLogout}>
                  <LogOut size={16} />
                  <span>Logout</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Layout Body */}
      <div className={styles.layoutBody}>
        {/* Desktop Sidebar */}
        <aside className={styles.sidebar}>
          <nav className={styles.sidebarNav}>
            {renderSidebarNav()}
          </nav>
          <div className={styles.sidebarLogout}>
            <button className={styles.sidebarLogoutBtn} onClick={handleLogout}>
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Area */}
        <div className={styles.mainArea}>
          {/* Grace Period Warning Banner */}
          {isInGracePeriod && (
            <div style={{
              backgroundColor: '#fffbeb',
              borderBottom: '1px solid #fcd34d',
              padding: '10px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#92400e'
            }}>
              <Clock size={16} />
              <span>Your business's subscription payment is overdue. Contact your administrator.</span>
            </div>
          )}

          {/* Subscription Lock Overlay */}
          {!hasFullAccess && <SubscriptionLockOverlay />}

          {/* Main Content */}
          <main className={styles.mainContent}>
            <div className={styles.contentContainer}>
              {children || <Outlet />}
            </div>
          </main>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div
        className={`${styles.mobileOverlay} ${isMobileMenuOpen ? styles.mobileOverlayOpen : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Sidebar Drawer */}
      <aside className={`${styles.mobileSidebar} ${isMobileMenuOpen ? styles.mobileSidebarOpen : ''}`}>
        <nav className={styles.sidebarNav}>
          {renderSidebarNav()}
        </nav>
        <div className={styles.sidebarLogout}>
          <button className={styles.sidebarLogoutBtn} onClick={handleLogout}>
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Notification Management Modal */}
      {isNotificationModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsNotificationModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>All Notifications</h2>
              <button
                className={styles.modalCloseBtn}
                onClick={() => setIsNotificationModalOpen(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className={styles.notificationFilters}>
              <div className={styles.notificationSearchWrapper}>
                <Search size={18} className={styles.notificationSearchIcon} />
                <input
                  type="text"
                  className={styles.notificationSearchInput}
                  placeholder="Search notifications..."
                  value={notificationSearch}
                  onChange={(e) => setNotificationSearch(e.target.value)}
                />
              </div>
              <select
                className={styles.notificationFilterSelect}
                value={notificationTypeFilter}
                onChange={(e) => setNotificationTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="checklist_assigned">Assigned</option>
                <option value="checklist_submitted">Submitted</option>
                <option value="checklist_reviewed">Reviewed</option>
              </select>
              <select
                className={styles.notificationFilterSelect}
                value={notificationReadFilter}
                onChange={(e) => setNotificationReadFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
              </select>
            </div>

            <div className={styles.modalBody}>
              {notifications.length === 0 ? (
                <div className={styles.modalEmptyState}>
                  <Bell size={64} className={styles.emptyIcon} />
                  <p className={styles.emptyText}>No notifications</p>
                  <p className={styles.emptySubtext}>You're all caught up!</p>
                </div>
              ) : filteredModalNotifications.length === 0 ? (
                <div className={styles.modalEmptyState}>
                  <Search size={48} className={styles.emptyIcon} />
                  <p className={styles.emptyText}>No matching notifications</p>
                  <p className={styles.emptySubtext}>Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className={styles.modalNotificationsList}>
                  {filteredModalNotifications.map(notification => (
                    <div
                      key={notification.id}
                      className={`${styles.modalNotificationItem} ${notification.read ? styles.read : styles.unread}`}
                    >
                      <div className={styles.notificationContent}>
                        <h4 className={styles.notificationTitle}>{notification.title}</h4>
                        <p className={styles.notificationMessage}>{notification.message}</p>
                        <span className={styles.notificationTime}>{notification.time}</span>
                      </div>
                      <div className={styles.notificationActions}>
                        <button
                          className={styles.dismissBtn}
                          onClick={() => handleDismissNotification(notification.id)}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button
                className={styles.btnSecondary}
                onClick={() => setIsNotificationModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Widget */}
      <SupportWidget />

      {/* Command Palette Modal */}
      {isSearchOpen && (
        <div
          className={styles.commandPaletteOverlay}
          onClick={() => setIsSearchOpen(false)}
        >
          <div
            className={styles.commandPalette}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.commandPaletteHeader}>
              <Search size={20} className={styles.commandSearchIcon} />
              <input
                type="text"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                className={styles.commandSearchInput}
                autoFocus
              />
              <kbd className={styles.escHint}>Esc</kbd>
            </div>

            <div className={styles.commandPaletteResults}>
              {filteredResults.length === 0 ? (
                <div className={styles.noResults}>
                  <p>No pages found</p>
                </div>
              ) : (
                <>
                  {searchQuery === '' && recentPages.length > 0 && (
                    <div className={styles.resultSection}>
                      <div className={styles.resultSectionLabel}>Recent</div>
                    </div>
                  )}
                  {filteredResults.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        className={`${styles.commandPaletteItem} ${index === selectedIndex ? styles.commandPaletteItemSelected : ''}`}
                        onClick={() => handleSearchNavigate(item)}
                      >
                        {Icon && typeof Icon === 'function' && <Icon size={18} className={styles.resultIcon} />}
                        <span className={styles.resultLabel}>{item.label}</span>
                        <span className={styles.resultPath}>{item.path}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            <div className={styles.commandPaletteFooter}>
              <div className={styles.commandHints}>
                <span><kbd>↑↓</kbd> Navigate</span>
                <span><kbd>Enter</kbd> Select</span>
                <span><kbd>Esc</kbd> Close</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeLayout;
